import React from 'react';
import { aiBridge } from './bridge';
import { buildAiConversationId } from './protocol';
import type { AiContext } from './protocol';

type ConnectionState = {
    checking: boolean;
    connected: boolean | null;
};

type GlobalAiConnectionCheckStore = {
    listeners: Set<(s: ConnectionState) => void>;
    state: ConnectionState;
    inFlight: Promise<boolean> | null;
    retryTimeoutId: number | null;
    checkAttempts: number;
};

const GLOBAL_KEY = '__ENTER_AI_CONNECTION_CHECK_STORE__';

const getGlobalStore = (): GlobalAiConnectionCheckStore => {
    const g = globalThis as any;
    if (!g[GLOBAL_KEY]) {
        g[GLOBAL_KEY] = {
            listeners: new Set<(s: ConnectionState) => void>(),
            state: {
                checking: false,
                connected: null,
            } satisfies ConnectionState,
            inFlight: null as Promise<boolean> | null,
            retryTimeoutId: null as number | null,
            checkAttempts: 0,
        } as GlobalAiConnectionCheckStore;
    }

    return g[GLOBAL_KEY] as GlobalAiConnectionCheckStore;
};

const store = getGlobalStore();

const notify = () => {
    for (const l of store.listeners) {
        l(store.state);
    }
};

export const checkAiExtensionConnection = async (timeoutMs: number = 8000): Promise<boolean> => {
    const MAX_CHECK_ATTEMPTS = 3;
    if (store.inFlight) {
        return store.inFlight;
    }

    if (store.retryTimeoutId !== null) {
        window.clearTimeout(store.retryTimeoutId);
        store.retryTimeoutId = null;
    }

    // If already checked, return cached value.
    if (store.state.connected === true && !store.state.checking) {
        return true;
    }

    // If extension is still disconnected after too many attempts - stop.
    if (store.state.connected === false && !store.state.checking && store.checkAttempts >= MAX_CHECK_ATTEMPTS) {
        return false;
    }

    store.checkAttempts += 1;
    store.state = { ...store.state, checking: true };
    notify();

    store.inFlight = (async () => {
        try {
            const context: AiContext = {
                entity: { name: 'ConnectionTest' },
            };

            await aiBridge.chat(
                buildAiConversationId('connectionCheck', 'ping'),
                'suggest',
                '__AI_PING__',
                context,
                'connection_check',
                timeoutMs,
            );
            store.state = { ...store.state, connected: true, checking: false };
            store.checkAttempts = 0;
            notify();
            return true;
        } catch {
            store.state = { ...store.state, connected: false, checking: false };
            notify();
            return false;
        } finally {
            store.inFlight = null;
        }
    })();

    return store.inFlight;
};

export const resetAiExtensionConnectionCheck = (): void => {
    if (store.retryTimeoutId !== null) {
        window.clearTimeout(store.retryTimeoutId);
        store.retryTimeoutId = null;
    }

    store.inFlight = null;
    store.checkAttempts = 0;
    store.state = { ...store.state, checking: false, connected: null };
    notify();
};

export const useAiExtensionConnectionStatus = (options?: { autoCheck?: boolean; retryOnFail?: boolean }) => {
    const { autoCheck = true, retryOnFail = true } = options ?? {};
    const [local, setLocal] = React.useState<ConnectionState>(() => store.state);

    React.useEffect(() => {
        const cb = (s: ConnectionState) => setLocal({ ...s });
        store.listeners.add(cb);
        return () => {
            store.listeners.delete(cb);
        };
    }, []);

    // First check on mount.
    React.useEffect(() => {
        if (!autoCheck) {
            return;
        }

        if (store.state.connected === null && !store.state.checking) {
            void checkAiExtensionConnection();
        }
    }, [autoCheck]);

    // If we previously failed to connect, retry after some delay.
    React.useEffect(() => {
        if (!retryOnFail) {
            return;
        }
        if (local.connected !== false || local.checking) {
            return;
        }

        // Guard: only one retry timeout scheduled globally.
        if (store.retryTimeoutId !== null) {
            return;
        }

        if (store.checkAttempts >= 3) {
            return;
        }

        store.retryTimeoutId = window.setTimeout(() => {
            store.retryTimeoutId = null;
            void checkAiExtensionConnection();
        }, 5000);
    }, [local.connected, local.checking, retryOnFail]);

    return local;
};

/**
 * Mount once at app startup to kick off the AI extension connection check.
 */
export const AiExtensionConnectionBootstrap: React.FC = () => {
    useAiExtensionConnectionStatus();
    return null;
};

